import React from 'react';
import { Phone, Wrench, CheckCircle, AlertCircle } from 'lucide-react';
export function AutomotiveTuneUp() {
  return <div className="min-h-screen bg-gray-50" data-id="element-811">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-812">
        <div className="container mx-auto px-4" data-id="element-813">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-814">Tune Up</h1>
          <p className="text-xl text-gray-100 italic" data-id="element-815">
            Serving Farmington Hills, Farmington, Novi, Livonia, Northville
            Since 1958
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-816">
        <div className="max-w-4xl mx-auto" data-id="element-817">
          {/* Introduction */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-818">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-819">
              <Wrench className="mr-3 text-[#B1050B]" size={32} data-id="element-820" />
              How can I tell if my car needs a tune-up?
            </h2>

            <p className="text-lg text-gray-700 mb-6" data-id="element-821">
              First, keep a running check on your gasoline mileage. Figure your
              average mileage for every three tankfuls. When this average has
              dropped by over 15%, chances are you need a tune-up or other work.
            </p>

            <p className="text-lg text-gray-700 mb-6" data-id="element-822">
              Other symptoms of an out-of-tune engine are listed below. Check
              each problem that occurs frequently with your car. The more
              problems you check, the more likely it is that your engine needs
              some sort of tune up.
            </p>

            <p className="text-lg font-semibold text-gray-900 mb-4" data-id="element-823">
              Check these problems that occur FREQUENTLY:
            </p>

            <div className="bg-gray-50 rounded-lg p-6 mb-6" data-id="element-824">
              <ol className="space-y-3 list-decimal list-inside" data-id="element-825">
                {['Idles fast when warm', 'Stalling', 'Low power', 'Idles rough', 'Knocking or pinging', 'Hard starting', 'Misfiring', 'Hesitation', 'Rough running', 'Engine runs on with key off', 'Black exhaust'].map((symptom, index) => <li key={index} className="text-gray-700 text-lg" data-id="element-826">
                    {symptom}
                  </li>)}
              </ol>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6" data-id="element-827">
              <p className="text-gray-800" data-id="element-828">
                <strong data-id="element-829">Caution:</strong> Be especially aware of changes in your
                car's behavior. Look for things it's doing today that it hasn't
                been doing. Some models, for instance, will exhibit hard
                starting, knock or run-on, even when they're perfectly tuned.
              </p>
            </div>
          </div>

          {/* What You Get Section */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-830">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-831">
              So what do you get for the for all them hours of labor?
            </h2>

            <p className="text-lg text-gray-700 mb-6" data-id="element-832">
              If you call our shop and ask for a car tune up price we
              automatically figure in the price a fuel filter, spark plugs, air
              filter, PCV filter, and distributor ignition rotor plus all
              applicable labor charges. So our car tune up price would probably
              be much higher than a shop that only replaces spark plugs as their
              auto tune up package. You need to make sure you are comparing
              apples to apples when you are calling around for prices.
            </p>

            <p className="text-lg text-gray-700 mb-6" data-id="element-833">
              The spark plugs are the business end of the ignition system. The
              spark plugs deliver the spark needed to ignite the air/fuel
              mixture. No spark means no combustion, wasted energy, increased
              emissions, loss of performance, idle roughness, hesitation, hard
              starting and possibly even a no start if all of the plugs are
              affected.
            </p>

            <h3 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-834">
              Optimal spark plug appearance
            </h3>
            <p className="text-gray-700 mb-6" data-id="element-835">
              The insulator nose is of a grey-white/grey-yellow to light
              red-brown colour. The electrode erosion is low. The thermal value
              of the spark plug has been chosen correctly. No thermal overload.
              The mixture and ignition settings are correct, the engine is in
              good condition.
            </p>
          </div>

          {/* Modern Ignition Systems */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-836">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-837">
              Modern Ignition Systems
            </h2>

            <p className="text-gray-700 mb-6" data-id="element-838">
              In modern systems, the distributor is omitted and ignition is
              instead electronically controlled. Much smaller coils are used
              with one coil for each spark plug or one coil serving two spark
              plugs (for example two coils in a four-cylinder engine, or three
              coils in a six-cylinder engine).
            </p>

            <p className="text-gray-700 mb-6" data-id="element-839">
              These coils may be remote-mounted or they may be placed on top of
              the spark plug (coil-on-plug or Direct Ignition). Where one coil
              serves two spark plugs (in two cylinders), it is through the
              "wasted spark" system. In this arrangement the coil generates two
              sparks per cycle to both the cylinders. The fuel in the cylinder
              that is nearing the end of its compression stroke is ignited,
              whereas the spark in its companion that is nearing the end of its
              exhaust stroke has no effect.
            </p>

            <p className="text-gray-700 mb-6" data-id="element-840">
              The wasted spark system is more reliable than a single coil system
              with a distributor and cheaper than coil-on-plug.
            </p>

            <p className="text-gray-700 mb-6" data-id="element-841">
              Where the coils are automatically mounted they may all be
              contained in a single moulded block with multiple high-tension
              terminals. This is commonly called a coil-pack.
            </p>

            <p className="text-gray-700 mb-6" data-id="element-842">
              Spark plug wires (also called "ignition cables") carry high
              voltage current from the ignition coil(s) to the spark plugs. On
              older engines with distributors, the wires run from the
              distributor cap to the plugs, and are different lengths to reach
              the closest and furthest spark plugs.
            </p>

            <p className="text-gray-700 mb-6" data-id="element-843">
              The wire set also includes an extra wire that connects the center
              terminal on the distributor cap to the ignition coil (unless the
              application has the coil mounted in the distributor cap. (GM HEI))
            </p>

            <p className="text-gray-700 mb-6" data-id="element-844">
              On engines with distributorless ignition systems (Waste Spark),
              plug wires are also used to connect the coils to their respective
              spark plugs. On some engines, there is one coil per spark plug,
              while on others with "waste spark" ignitions, two plugs share each
              coil.
            </p>

            <p className="text-gray-700 mb-6" data-id="element-845">
              On coil-on-plug (COP) (Ford Triton) ignition systems, there are no
              plug wires because the coils are mounted directly on top of the
              spark plugs. But in coil-near-plug (CNP) systems, there are short
              wires that connect the coils and plugs. On some applications,
              these wires are part of each coil assembly and cannot be replaced
              separately.
            </p>
          </div>

          {/* Closing Statement */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-846">
            <h2 className="text-2xl font-bold text-[#B1050B] mb-4" data-id="element-847">
              If We Can Help Give Us A Call 248-478-8382
            </h2>
            <p className="text-gray-700" data-id="element-848">
              <strong data-id="element-849">Tune Up</strong> - Want to save money on fuel? Want to
              make sure your car is as eco-friendly as it can be? It's easier
              than your think. You need to get your car or truck a tune up.
              Garrett Auto and Truck knows how to get the most efficiency out of
              your auto, passenger car or truck, by performing a high quality
              tune up, using the best technology, training, parts and service to
              give your auto the best tune up possible.
            </p>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-850">
            <h2 className="text-3xl font-bold mb-4" data-id="element-851">Need a Tune-Up?</h2>
            <p className="text-xl mb-6" data-id="element-852">
              Contact Garrett Auto & Truck Service today for expert automotive
              tune-up services
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-853">
              <Phone className="mr-3" size={24} data-id="element-854" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-855">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}